"use client";

import {
  BoardCardDeleteButton,
  BoardCardItemView,
  BoardCardListView,
  BoardCardMoveView,
  BoardCardView,
  BoardColumnEmptyView,
  BoardColumnPickerView,
  BoardColumnView,
  RecordBoardLensView,
} from "@product/app/workspace/channels/[channelId]/record-board-view";

import type { ShotBoard } from "./channel-board";
import { ShotCellValue } from "./channel-grid";

type Column = ShotBoard["columns"][number];

/**
 * A board column at rest, as BoardColumn draws it: the header with its
 * accent, count and subtotal, then the cards stacked in order (where the
 * measured cards land), each opening its record, with its move control
 * where the grouping field allows moves and its delete button.
 */
function ShotBoardColumn({ column, mobile = false }: { column: Column; mobile?: boolean }) {
  return (
    <BoardColumnView
      title={column.title}
      color={column.color}
      count={column.count}
      subtotal={column.subtotal}
      mobile={mobile}
      className="mlx-floating-panel-scroll-surface"
    >
      <BoardCardListView>
        {column.cards.map((card, index) => (
          <BoardCardItemView key={card.id} index={index}>
            <BoardCardView
              title={card.title}
              sumText={card.sumText}
              chips={
                card.chips.length
                  ? card.chips.map((cell, chip) =>
                      cell ? (
                        <span key={chip} className="truncate">
                          <ShotCellValue cell={cell} />
                        </span>
                      ) : null,
                    )
                  : undefined
              }
              openable
              draggable={!mobile}
              aria-busy={false}
              className={mobile ? "cursor-default" : "cursor-grab active:cursor-grabbing"}
              footer={card.moveTargets.length ? <BoardCardMoveView targets={card.moveTargets} mobile={mobile} /> : null}
              actions={<BoardCardDeleteButton mobile={mobile} />}
            />
          </BoardCardItemView>
        ))}
      </BoardCardListView>
      {column.cards.length === 0 ? <BoardColumnEmptyView>No records in this column.</BoardColumnEmptyView> : null}
    </BoardColumnView>
  );
}

/**
 * A board view at rest, as RecordBoardLens draws it: on a phone the column
 * picker over its first column; from `sm` the columns side by side.
 */
export function ShotBoardView({ board }: { board: ShotBoard }) {
  const first = board.columns[0];
  if (!first) throw new Error("A board shows at least one column.");
  return (
    <RecordBoardLensView
      picker={
        <BoardColumnPickerView
          columns={board.columns.map(({ key, title, count }) => ({ key, title, count }))}
          selected={first.key}
        />
      }
      mobileColumn={<ShotBoardColumn column={first} mobile />}
    >
      {board.columns.map((column) => (
        <ShotBoardColumn key={column.key} column={column} />
      ))}
    </RecordBoardLensView>
  );
}
